/**
 * setOperatorChainSummary — pure "chain badge" summarizer for a top-level
 * set-operator chain (COM-17, COM-30..33: nothing in the first viewport
 * signals a UNION/INTERSECT/EXCEPT(/MINUS) chain, since the operator pill +
 * branches 2..N sit below the fold). `summarizeSetOperatorChain` turns a
 * `SetOperatorOutline` into a short, TRUTHFUL-per-engine header string —
 * spelled via `setOperatorChoicesForEngine` so Oracle's third operator says
 * MINUS (never a hardcoded EXCEPT), and `UNION ALL` is preserved.
 *
 * No React — rendered by `SetOperatorBranches.tsx` as the first child of the
 * chain, top-level chains only (gated by the ABSENCE of `branchLabels`, which
 * only CTE recursive-body mounts pass — see that component's docblock).
 *
 * Mirrors `compact/compactDegradeDetail.ts`'s `setOperatorDetail()` operator
 * labeling (same per-engine choice lookup) so the compact degrade notice and
 * this full-mode header never describe the same chain two different ways —
 * duplicated rather than imported because `compact/` is owned by a
 * concurrent lane.
 * TODO(consolidate): shared with compactDegradeDetail.setOperatorDetail's operatorLabel.
 */
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { SetOperatorOutline, SetOpKind } from './extractSetOperatorBranches';
import { setOperatorChoicesForEngine } from './setOperatorKindMaps';

function operatorLabel(kind: SetOpKind, isAll: boolean, engine: SqlEngine): string {
  const choices = setOperatorChoicesForEngine(engine);
  const match = choices.find((c) => c.kind === kind && c.all === isAll);
  return match ? match.label : `${kind}${isAll ? ' ALL' : ''}`;
}

/**
 * Composes the chain-summary header text. Single distinct operator (across
 * all separators, ALL-modifier included in the identity) yields
 * `"N-branch <LABEL> chain"` (e.g. `"3-branch UNION ALL chain"`); a mix of
 * distinct operators yields `"N-branch chain · <LABEL> / <LABEL>"`, listing
 * each distinct label once in first-seen declaration order.
 */
export function summarizeSetOperatorChain(outline: SetOperatorOutline, engine: SqlEngine): string {
  const branchCount = outline.branches.length;
  const labels: string[] = [];
  for (const sep of outline.separators) {
    const label = operatorLabel(sep.kind, sep.isAll, engine);
    if (!labels.includes(label)) labels.push(label);
  }
  if (labels.length <= 1) {
    const label = labels[0] ?? 'set-operator';
    return `${branchCount}-branch ${label} chain`;
  }
  return `${branchCount}-branch chain · ${labels.join(' / ')}`;
}
